import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  TextField,
  Divider,
  Link,
  IconButton,
  InputAdornment,
} from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import useAuthStore from '../store/authStore'
import { consumeAdminPortalAuthNotice } from '../utils/authSessionNotice'
import {
  capturePostLoginRedirect,
  consumePostLoginRedirect,
  sanitizeInternalPath,
} from '../utils/authRedirect'
import { authService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { brandColors } from '../brandColors'

const LoginPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const token = useAuthStore((state) => state.token)
  const setToken = useAuthStore((state) => state.setToken)
  const [processing, setProcessing] = useState(false)
  const [showAuthBarrier, setShowAuthBarrier] = useState(false)
  const [sessionDetail, setSessionDetail] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState('')
  const didPostLoginRedirectRef = useRef(false)

  const authReason = searchParams.get('reason')
  const authHint = searchParams.get('hint')
  const nextFromQuery = searchParams.get('next')

  useEffect(() => {
    const safeNext = sanitizeInternalPath(nextFromQuery)
    if (safeNext) {
      capturePostLoginRedirect(safeNext)
    }
  }, [nextFromQuery])

  useEffect(() => {
    if (authReason === 'auth_failed') {
      setShowAuthBarrier(true)
      if (authHint) {
        try {
          setSessionDetail(decodeURIComponent(authHint))
        } catch {
          setSessionDetail(authHint)
        }
      }
      const keep = new URLSearchParams()
      const safeNext = sanitizeInternalPath(nextFromQuery)
      if (safeNext) keep.set('next', safeNext)
      setSearchParams(keep, { replace: true })
      return
    }
    const notice = consumeAdminPortalAuthNotice()
    if (notice) {
      setShowAuthBarrier(true)
      setSessionDetail(notice.detail || '')
    }
  }, [authReason, authHint, nextFromQuery, setSearchParams])

  useEffect(() => {
    if (!token || didPostLoginRedirectRef.current) return
    didPostLoginRedirectRef.current = true
    navigate(consumePostLoginRedirect(), { replace: true })
  }, [token, navigate])

  const handleGoogleSignIn = () => {
    setProcessing(true)
    const safeNext = sanitizeInternalPath(nextFromQuery)
    if (safeNext) {
      capturePostLoginRedirect(safeNext)
    }
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
    window.location.href = `${backendUrl}/admin/auth/google`
  }

  const handlePasswordLogin = async (e) => {
    e.preventDefault()
    setLoginError('')
    setProcessing(true)
    try {
      const data = await authService.passwordLogin(email.trim(), password)
      // Token effect consumes post-login redirect (shared link / prior page).
      setToken(data.access_token)
    } catch (err) {
      setLoginError(getApiErrorMessage(err, 'Invalid email or password.'))
      setProcessing(false)
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        p: 2,
        py: { xs: 4, sm: 6 },
        boxSizing: 'border-box',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            width: '100%',
            maxWidth: 440,
            px: 3,
            py: 3.5,
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
            my: { xs: 2, sm: 0 },
            flexShrink: 0,
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(15,18,28,0.72)',
            backdropFilter: 'saturate(160%) blur(8px)',
          }}
        >
          <CardContent>
            {showAuthBarrier ? (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  textAlign: 'left',
                  bgcolor: 'rgba(211, 47, 47, 0.12)',
                  color: 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '& .MuiAlert-icon': { color: '#ff8a80' },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Could not open the admin portal with this session
                </Typography>
                <Typography variant="body2" sx={{ mb: sessionDetail ? 1 : 0, lineHeight: 1.55 }}>
                  You may have used Google sign-in from another application, your session may have
                  expired, or your Google account may not be on the authorized list for this portal.
                  Use <strong>Continue with Google</strong> or email/password on this page only.
                </Typography>
                {sessionDetail ? (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ display: 'block', opacity: 0.85, fontStyle: 'italic', mt: 0.5 }}
                  >
                    {sessionDetail}
                  </Typography>
                ) : null}
              </Alert>
            ) : null}

            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: brandColors.primary }}>
                Shwapno
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                Admin Portal
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                Manage inventory, products, and view comprehensive reports
              </Typography>
            </Box>

            <Box component="form" onSubmit={handlePasswordLogin} sx={{ mb: 2 }}>
              {loginError ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {loginError}
                </Alert>
              ) : null}
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                sx={{ mb: 1.5, input: { color: '#fff' }, label: { color: 'rgba(255,255,255,0.7)' } }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                sx={{ mb: 1.5, input: { color: '#fff' }, label: { color: 'rgba(255,255,255,0.7)' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={processing}
                sx={{ py: 1.25, fontWeight: 700, mb: 1 }}
              >
                Sign in
              </Button>
              <Typography align="center" sx={{ fontSize: 13 }}>
                <Link component={RouterLink} to="/forgot-password" sx={{ color: '#90caf9' }}>
                  Forgot password?
                </Link>
              </Typography>
            </Box>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>
              OR
            </Divider>

            <Button
              onClick={handleGoogleSignIn}
              variant="contained"
              fullWidth
              startIcon={<GoogleIcon />}
              sx={{
                mb: 1.25,
                py: 1.25,
                fontSize: 16,
                fontWeight: 700,
                backgroundColor: '#4285f4',
                '&:hover': { backgroundColor: '#357ae8' },
              }}
              disabled={processing}
            >
              Continue with Google
            </Button>

            <Typography align="center" sx={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
              Existing Google-only admins can continue with Google.
            </Typography>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', textAlign: 'center', mt: 2 }}>
              Only whitelisted admins can access this portal. New admins must be invited.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}

export default LoginPage
